import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Repeat, Loader2 } from "lucide-react";

const INVALIDATE = ["roster", "roster-pending", "cleaners", "roster-day", "roster-day-labels", "timeline", "cleans", "timesheets"];

export function RosterTools({ data, shiftDate }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refresh = () => INVALIDATE.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const copy = useMutation({
    mutationFn: () => api.post("/roster/copy", { from_date: shiftDate(data.start, -data.days.length), to_date: data.start }),
    onSuccess: (r) => { refresh(); toast.success(t("Copied previous period — {{n}} day(s) changed. Review and Save / send.", { n: r.data.changed })); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const setDefault = useMutation({
    mutationFn: () => api.post("/roster/set-default", { date: data.start }),
    onSuccess: () => { refresh(); toast.success(t("This week is now the standing pattern for all future weeks")); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!data || data.locked) return null;
  const busy = copy.isPending || setDefault.isPending;
  return (
    <span className="flex gap-1">
      <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy}
        onClick={() => window.confirm(t("Copy the previous period's ON/OFF days onto {{period}}? Leave and absences are kept.", { period: `${fmtDMY(data.start)} – ${fmtDMY(data.end)}` })) && copy.mutate()}
        data-testid="roster-copy-prev">
        {copy.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />} {t("Copy last period")}
      </Button>
      <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy}
        onClick={() => window.confirm(t("Use this week's ON/OFF days as every cleaner's standing pattern for all future weeks? One-off changes on later dates will be cleared; leave is kept.")) && setDefault.mutate()}
        data-testid="roster-set-default">
        {setDefault.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Repeat className="h-3.5 w-3.5" />} {t("Repeat every week")}
      </Button>
    </span>
  );
}
